import { useMemo, useState } from "react";
import { useParams } from "wouter";
import { ArrowDown, ArrowUp, BookOpen, ClipboardCheck, Eye, EyeOff, GraduationCap, Pencil, Plus, Trash2, X } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { StandardTabsHeader } from "@/components/StandardTabsHeader";
import { useDemoStore } from "@/store/useDemoStore";
import { useStandard, useElementsForStandard, useLmsRoute } from "@/store/hooks";
import type { LmsBlock, LmsModule, LmsModuleKind, LmsRoute } from "@/data/seed";

const KIND_OPTIONS: { value: LmsModuleKind; label: string; tone: "gray" | "navy" | "teal" }[] = [
  { value: "leccion", label: "Lección", tone: "gray" },
  { value: "simulador-preop", label: "Simulador", tone: "navy" },
  { value: "evaluacion-final", label: "Evaluación", tone: "teal" },
];

type BlockDraft = { id?: string; titulo: string; descripcion: string };
type ModuleDraft = {
  id?: string;
  blockId: string;
  titulo: string;
  descripcion: string;
  kind: LmsModuleKind;
  duracionMin: number;
  criteriosCubiertos: string[];
  elementoId: string; // "" = sin asignar (transversal)
};

export default function LmsEditorPage() {
  const params = useParams<{ codigo: string }>();
  const codigo = params.codigo ?? "";
  const standard = useStandard(codigo);
  const elements = useElementsForStandard(codigo);
  const route = useLmsRoute(codigo);
  const setLmsRoute = useDemoStore((s) => s.setLmsRoute);
  const logEvent = useDemoStore((s) => s.logEvent);
  const activeRole = useDemoStore((s) => s.activeRole);

  const [blockDraft, setBlockDraft] = useState<BlockDraft | null>(null);
  const [moduleDraft, setModuleDraft] = useState<ModuleDraft | null>(null);
  const [confirm, setConfirm] = useState<{ kind: "block" | "module"; id: string; label: string } | null>(null);

  const criterioLabel = useMemo(() => {
    const m: Record<string, { code: string; elementId: string; elementCode: string; descripcion: string }> = {};
    for (const el of elements) {
      for (const c of el.criterios) m[c.id] = { code: c.codigo, elementId: el.id, elementCode: el.codigo, descripcion: c.descripcion };
    }
    return m;
  }, [elements]);

  // Devuelve el elemento del módulo: usa elementoId si está, si no lo deriva de los criterios cubiertos.
  function resolveModuleElement(m: LmsModule): { id: string; codigo: string } | null {
    if (m.elementoId) {
      const el = elements.find((e) => e.id === m.elementoId);
      if (el) return { id: el.id, codigo: el.codigo };
    }
    for (const cid of m.criteriosCubiertos ?? []) {
      const cm = criterioLabel[cid];
      if (cm) {
        const el = elements.find((e) => e.id === cm.elementId);
        if (el) return { id: el.id, codigo: el.codigo };
      }
    }
    return null;
  }

  if (!standard) {
    return (
      <AppLayout breadcrumb={["Estándares", codigo, "Ruta formativa"]}>
        <Card>
          <SectionTitle>Estándar no encontrado</SectionTitle>
        </Card>
      </AppLayout>
    );
  }

  function commit(next: LmsRoute, accion: string) {
    setLmsRoute(codigo, next);
    logEvent({ actor: activeRole, rol: activeRole, accion, entidad: "standard", entidadId: codigo });
  }

  function createEmptyRoute(): LmsRoute {
    return { estandar: codigo, publicada: false, blocks: [], modules: [] };
  }

  const current: LmsRoute = route ?? createEmptyRoute();
  const blocks = [...current.blocks].sort((a, b) => a.orden - b.orden);
  const totalMin = current.modules.reduce((a, m) => a + m.duracionMin, 0);
  const coveredCrits = new Set<string>();
  for (const m of current.modules) for (const c of m.criteriosCubiertos ?? []) coveredCrits.add(c);
  const totalCrits = Object.keys(criterioLabel).length;

  function togglePublish() {
    commit({ ...current, publicada: !current.publicada }, current.publicada ? "Ruta formativa despublicada" : "Ruta formativa publicada");
  }

  function openNewBlock() {
    setBlockDraft({ titulo: "", descripcion: "" });
  }
  function openEditBlock(b: LmsBlock) {
    setBlockDraft({ id: b.id, titulo: b.titulo, descripcion: b.descripcion });
  }
  function saveBlock() {
    if (!blockDraft || !blockDraft.titulo.trim()) return;
    if (blockDraft.id) {
      const next = { ...current, blocks: current.blocks.map((b) => b.id === blockDraft.id ? { ...b, titulo: blockDraft.titulo.trim(), descripcion: blockDraft.descripcion.trim() } : b) };
      commit(next, `Bloque editado: ${blockDraft.titulo}`);
    } else {
      const id = `b${Date.now()}`;
      const orden = (blocks[blocks.length - 1]?.orden ?? 0) + 1;
      const newBlock: LmsBlock = { id, orden, titulo: blockDraft.titulo.trim(), descripcion: blockDraft.descripcion.trim() };
      commit({ ...current, blocks: [...current.blocks, newBlock] }, `Bloque creado: ${newBlock.titulo}`);
    }
    setBlockDraft(null);
  }
  function deleteBlock(id: string) {
    const next = { ...current, blocks: current.blocks.filter((b) => b.id !== id), modules: current.modules.filter((m) => m.blockId !== id) };
    commit(next, "Bloque eliminado");
  }
  function moveBlock(id: string, dir: -1 | 1) {
    const idx = blocks.findIndex((b) => b.id === id);
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= blocks.length) return;
    const a = blocks[idx], b = blocks[tgt];
    const next = { ...current, blocks: current.blocks.map((x) => x.id === a.id ? { ...x, orden: b.orden } : x.id === b.id ? { ...x, orden: a.orden } : x) };
    commit(next, "Reordenamiento de bloques");
  }

  function openNewModule(blockId: string) {
    setModuleDraft({ blockId, titulo: "", descripcion: "", kind: "leccion", duracionMin: 10, criteriosCubiertos: [], elementoId: "" });
  }
  function openEditModule(m: LmsModule) {
    const auto = resolveModuleElement(m);
    setModuleDraft({
      id: m.id, blockId: m.blockId, titulo: m.titulo, descripcion: m.descripcion, kind: m.kind, duracionMin: m.duracionMin,
      criteriosCubiertos: [...(m.criteriosCubiertos ?? [])],
      elementoId: m.elementoId ?? auto?.id ?? "",
    });
  }
  function saveModule() {
    if (!moduleDraft || !moduleDraft.titulo.trim()) return;
    const elementoId = moduleDraft.elementoId || undefined;
    if (moduleDraft.id) {
      const next = { ...current, modules: current.modules.map((m) => m.id === moduleDraft.id ? { ...m, titulo: moduleDraft.titulo.trim(), descripcion: moduleDraft.descripcion.trim(), kind: moduleDraft.kind, duracionMin: moduleDraft.duracionMin, criteriosCubiertos: moduleDraft.criteriosCubiertos, elementoId } : m) };
      commit(next, `Módulo editado: ${moduleDraft.titulo}`);
    } else {
      const id = `m${Date.now()}`;
      const siblings = current.modules.filter((m) => m.blockId === moduleDraft.blockId);
      const orden = (Math.max(0, ...siblings.map((m) => m.orden))) + 1;
      const newModule: LmsModule = {
        id, blockId: moduleDraft.blockId, orden,
        titulo: moduleDraft.titulo.trim(), descripcion: moduleDraft.descripcion.trim(),
        kind: moduleDraft.kind, duracionMin: moduleDraft.duracionMin,
        contenido: [], quiz: [], criteriosCubiertos: moduleDraft.criteriosCubiertos,
        elementoId,
      };
      commit({ ...current, modules: [...current.modules, newModule] }, `Módulo creado: ${newModule.titulo}`);
    }
    setModuleDraft(null);
  }
  function deleteModule(id: string) {
    commit({ ...current, modules: current.modules.filter((m) => m.id !== id) }, "Módulo eliminado");
  }
  function moveModule(blockId: string, id: string, dir: -1 | 1) {
    const siblings = current.modules.filter((m) => m.blockId === blockId).sort((a, b) => a.orden - b.orden);
    const idx = siblings.findIndex((m) => m.id === id);
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= siblings.length) return;
    const a = siblings[idx], b = siblings[tgt];
    const next = { ...current, modules: current.modules.map((x) => x.id === a.id ? { ...x, orden: b.orden } : x.id === b.id ? { ...x, orden: a.orden } : x) };
    commit(next, "Reordenamiento de módulos");
  }

  const headerActions = (
    <PrimaryButton
      size="sm"
      variant={current.publicada ? "outline" : undefined}
      onClick={togglePublish}
      testId="button-lms-publish-toggle"
    >
      {current.publicada ? <><EyeOff size={12} /> Despublicar ruta</> : <><Eye size={12} /> Publicar ruta</>}
    </PrimaryButton>
  );

  return (
    <AppLayout breadcrumb={["Estándares", codigo, "Ruta formativa"]}>
      <StandardTabsHeader standard={standard} active="lms" rightAction={headerActions} />

      <Card testId="admin-lms-editor">
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <Badge tone={current.publicada ? "green" : "amber"}>{current.publicada ? "✓ Publicada" : "Borrador"}</Badge>
              <span style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>Ruta formativa</span>
            </div>
            <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>
              {blocks.length} bloques · {current.modules.length} módulos · ~{totalMin} min ·
              {" "}cubre {coveredCrits.size}/{totalCrits} criterios del estándar · umbral “Listo para evaluación” 80%
            </div>
          </div>
          <PrimaryButton size="sm" onClick={openNewBlock} testId="button-add-block">
            <Plus size={12} /> Agregar bloque
          </PrimaryButton>
        </div>

        {blocks.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--cc-gray-500)", fontSize: 13, border: "1px dashed var(--cc-gray-200)", borderRadius: 8 }}>
            <BookOpen size={20} style={{ marginBottom: 6, opacity: 0.6 }} />
            <div>Esta ruta aún no tiene bloques. Agrega el primero para empezar.</div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {blocks.map((b, bi) => {
              const mods = current.modules.filter((m) => m.blockId === b.id).sort((a, b) => a.orden - b.orden);
              return (
                <div key={b.id} data-testid={`block-${b.id}`} style={{ padding: 12, border: "1px solid var(--cc-gray-200)", borderRadius: 8, background: "white" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                        <span style={{ background: "var(--cc-navy)", color: "white", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 700 }}>{b.orden}</span>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }}>{b.titulo}</span>
                      </div>
                      <div style={{ fontSize: 12, color: "var(--cc-gray-600)" }}>{b.descripcion}</div>
                    </div>
                    <div style={{ display: "inline-flex", gap: 4, flexShrink: 0 }}>
                      <button aria-label="Subir" disabled={bi === 0} onClick={() => moveBlock(b.id, -1)} style={iconBtn(bi === 0)} data-testid={`block-up-${b.id}`}><ArrowUp size={12} /></button>
                      <button aria-label="Bajar" disabled={bi === blocks.length - 1} onClick={() => moveBlock(b.id, 1)} style={iconBtn(bi === blocks.length - 1)} data-testid={`block-down-${b.id}`}><ArrowDown size={12} /></button>
                      <button aria-label="Editar" onClick={() => openEditBlock(b)} style={iconBtn()} data-testid={`block-edit-${b.id}`}><Pencil size={12} /></button>
                      <button aria-label="Eliminar" onClick={() => setConfirm({ kind: "block", id: b.id, label: b.titulo })} style={iconBtn(false, "#dc2626")} data-testid={`block-delete-${b.id}`}><Trash2 size={12} /></button>
                    </div>
                  </div>

                  <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                    {mods.length === 0 && (
                      <div style={{ fontSize: 12, color: "var(--cc-gray-400)", fontStyle: "italic", padding: "6px 10px" }}>
                        Sin módulos aún.
                      </div>
                    )}
                    {mods.map((m, mi) => {
                      const kindMeta = KIND_OPTIONS.find((k) => k.value === m.kind);
                      const elemMeta = resolveModuleElement(m);
                      return (
                        <div key={m.id} data-testid={`module-${m.id}`} style={{ padding: 8, background: "var(--cc-gray-50, #fafbfc)", borderRadius: 6, display: "flex", alignItems: "flex-start", gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <Badge tone={kindMeta?.tone ?? "gray"}>{kindMeta?.label ?? m.kind}</Badge>
                              {elemMeta
                                ? <Badge tone="navy">{elemMeta.codigo}</Badge>
                                : <Badge tone="gray">Transversal</Badge>}
                              <span style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{m.titulo}</span>
                              <span style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>· {m.duracionMin} min</span>
                            </div>
                            {m.criteriosCubiertos && m.criteriosCubiertos.length > 0 && (
                              <div style={{ marginTop: 4, display: "flex", flexWrap: "wrap", gap: 3, alignItems: "center" }}>
                                <span style={{ fontSize: 10, color: "var(--cc-gray-500)", fontWeight: 600, textTransform: "uppercase", marginRight: 4 }}>Criterios:</span>
                                {m.criteriosCubiertos.map((cid) => {
                                  const cm = criterioLabel[cid];
                                  if (cm) return <Badge key={cid} tone="teal">{`${cm.elementCode}·${cm.code}`}</Badge>;
                                  return (
                                    <span key={cid} title="Referencia desactualizada (el criterio ya no existe en el estándar)" style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#fef3c7", color: "#92400e", fontWeight: 600 }}>
                                      {cid} ⚠
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div style={{ display: "inline-flex", gap: 4, flexShrink: 0 }}>
                            <button aria-label="Subir" disabled={mi === 0} onClick={() => moveModule(b.id, m.id, -1)} style={iconBtn(mi === 0)} data-testid={`module-up-${m.id}`}><ArrowUp size={12} /></button>
                            <button aria-label="Bajar" disabled={mi === mods.length - 1} onClick={() => moveModule(b.id, m.id, 1)} style={iconBtn(mi === mods.length - 1)} data-testid={`module-down-${m.id}`}><ArrowDown size={12} /></button>
                            <button aria-label="Editar" onClick={() => openEditModule(m)} style={iconBtn()} data-testid={`module-edit-${m.id}`}><Pencil size={12} /></button>
                            <button aria-label="Eliminar" onClick={() => setConfirm({ kind: "module", id: m.id, label: m.titulo })} style={iconBtn(false, "#dc2626")} data-testid={`module-delete-${m.id}`}><Trash2 size={12} /></button>
                          </div>
                        </div>
                      );
                    })}
                    <button onClick={() => openNewModule(b.id)} style={addModuleBtn} data-testid={`button-add-module-${b.id}`}>
                      <Plus size={12} /> Agregar módulo a este bloque
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {blockDraft && (
        <Modal title={blockDraft.id ? "Editar bloque" : "Nuevo bloque"} icon={<BookOpen size={16} />} onClose={() => setBlockDraft(null)} onSave={saveBlock} saveDisabled={!blockDraft.titulo.trim()}>
          <Field label="Título del bloque *">
            <input value={blockDraft.titulo} onChange={(e) => setBlockDraft({ ...blockDraft, titulo: e.target.value })} style={inputStyle()} placeholder="Ej. Seguridad y prevención" data-testid="input-block-titulo" />
          </Field>
          <Field label="Descripción">
            <textarea value={blockDraft.descripcion} onChange={(e) => setBlockDraft({ ...blockDraft, descripcion: e.target.value })} style={{ ...inputStyle(), minHeight: 70, resize: "vertical", fontFamily: "inherit" }} placeholder="Qué se aprende en este bloque" data-testid="input-block-descripcion" />
          </Field>
        </Modal>
      )}

      {moduleDraft && (
        <Modal title={moduleDraft.id ? "Editar módulo" : "Nuevo módulo"} icon={<GraduationCap size={16} />} onClose={() => setModuleDraft(null)} onSave={saveModule} saveDisabled={!moduleDraft.titulo.trim()}>
          <Field label="Título del módulo *">
            <input value={moduleDraft.titulo} onChange={(e) => setModuleDraft({ ...moduleDraft, titulo: e.target.value })} style={inputStyle()} placeholder="Ej. Inspección visual de neumáticos" data-testid="input-module-titulo" />
          </Field>
          <Field label="Descripción breve">
            <textarea value={moduleDraft.descripcion} onChange={(e) => setModuleDraft({ ...moduleDraft, descripcion: e.target.value })} style={{ ...inputStyle(), minHeight: 60, resize: "vertical", fontFamily: "inherit" }} data-testid="input-module-descripcion" />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <Field label="Tipo de módulo *">
              <select value={moduleDraft.kind} onChange={(e) => setModuleDraft({ ...moduleDraft, kind: e.target.value as LmsModuleKind })} style={inputStyle()} data-testid="select-module-kind">
                {KIND_OPTIONS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </Field>
            <Field label="Elemento del estándar">
              <select
                value={moduleDraft.elementoId}
                onChange={(e) => {
                  const newElemId = e.target.value;
                  const allowedCritIds = newElemId
                    ? new Set((elements.find((el) => el.id === newElemId)?.criterios ?? []).map((c) => c.id))
                    : null;
                  const filteredCrits = allowedCritIds
                    ? moduleDraft.criteriosCubiertos.filter((cid) => allowedCritIds.has(cid))
                    : moduleDraft.criteriosCubiertos;
                  setModuleDraft({ ...moduleDraft, elementoId: newElemId, criteriosCubiertos: filteredCrits });
                }}
                style={inputStyle()}
                data-testid="select-module-elemento"
              >
                <option value="">Transversal (ninguno)</option>
                {elements.map((el) => <option key={el.id} value={el.id}>{el.codigo} — {el.titulo}</option>)}
              </select>
            </Field>
            <Field label="Duración (min) *">
              <input type="number" min={1} max={600} value={moduleDraft.duracionMin} onChange={(e) => setModuleDraft({ ...moduleDraft, duracionMin: Math.max(1, Number(e.target.value) || 1) })} style={inputStyle()} data-testid="input-module-duracion" />
            </Field>
          </div>
          <Field label={moduleDraft.elementoId ? "Criterios cubiertos (filtrados por el elemento seleccionado)" : "Criterios cubiertos"}>
            <div style={{ border: "1px solid var(--cc-gray-200)", borderRadius: 6, padding: 8, maxHeight: 240, overflowY: "auto", background: "white" }}>
              {elements.length === 0 && <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>Este estándar no tiene elementos cargados.</div>}
              {elements
                .filter((el) => !moduleDraft.elementoId || el.id === moduleDraft.elementoId)
                .map((el) => (
                <div key={el.id} style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 4 }}>{el.codigo} — {el.titulo}</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {el.criterios.map((c) => {
                      const checked = moduleDraft.criteriosCubiertos.includes(c.id);
                      return (
                        <label key={c.id} style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 12, color: "var(--cc-gray-700)", cursor: "pointer", padding: "2px 4px" }} data-testid={`crit-toggle-${c.id}`}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...moduleDraft.criteriosCubiertos, c.id]
                                : moduleDraft.criteriosCubiertos.filter((x) => x !== c.id);
                              setModuleDraft({ ...moduleDraft, criteriosCubiertos: next });
                            }}
                            style={{ marginTop: 2 }}
                          />
                          <span><strong style={{ color: "var(--cc-navy)" }}>{el.codigo}·{c.codigo}.</strong> {c.descripcion}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </Field>
          <div style={{ fontSize: 11, color: "var(--cc-gray-500)", display: "flex", alignItems: "center", gap: 6 }}>
            <ClipboardCheck size={12} /> Las lecciones nuevas inician sin contenido textual ni reactivos; podrás cargarlos en una próxima iteración.
          </div>
        </Modal>
      )}

      {confirm && (
        <ConfirmModal
          title={confirm.kind === "block" ? "Eliminar bloque" : "Eliminar módulo"}
          message={confirm.kind === "block"
            ? <>¿Eliminar el bloque <strong>{confirm.label}</strong>? También se eliminarán todos sus módulos.</>
            : <>¿Eliminar el módulo <strong>{confirm.label}</strong>?</>}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            if (confirm.kind === "block") deleteBlock(confirm.id);
            else deleteModule(confirm.id);
            setConfirm(null);
          }}
        />
      )}
    </AppLayout>
  );
}

function Modal({ title, icon, onClose, onSave, saveDisabled, children }: {
  title: string;
  icon: React.ReactNode;
  onClose: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalBox} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 20px", borderBottom: "1px solid var(--cc-gray-100)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--cc-teal-bg)", color: "var(--cc-teal)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>{icon}</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>{title}</div>
          </div>
          <button onClick={onClose} style={iconBtn()} aria-label="Cerrar"><X size={16} /></button>
        </div>
        <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>{children}</div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--cc-gray-100)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={ghostBtn} data-testid="button-modal-cancel">Cancelar</button>
          <PrimaryButton size="sm" onClick={onSave} disabled={saveDisabled} testId="button-modal-save">Guardar</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

function ConfirmModal({ title, message, onCancel, onConfirm }: { title: string; message: React.ReactNode; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div style={overlay} onClick={onCancel}>
      <div style={{ ...modalBox, maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)", marginBottom: 8 }}>{title}</div>
          <div style={{ fontSize: 13, color: "var(--cc-gray-700)", lineHeight: 1.5 }}>{message}</div>
        </div>
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--cc-gray-100)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onCancel} style={ghostBtn} data-testid="button-confirm-cancel">Cancelar</button>
          <button onClick={onConfirm} style={{ ...ghostBtn, background: "#dc2626", color: "white", border: "1px solid #dc2626" }} data-testid="button-confirm-ok">Eliminar</button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--cc-gray-500)", fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function inputStyle(): React.CSSProperties {
  return { width: "100%", padding: "8px 10px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 13, background: "white" };
}

function iconBtn(disabled = false, color?: string): React.CSSProperties {
  return {
    width: 26, height: 26, border: "1px solid var(--cc-gray-200)", background: "white", borderRadius: 6,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    color: color ?? "var(--cc-gray-700)",
    opacity: disabled ? 0.4 : 1,
  };
}

const ghostBtn: React.CSSProperties = {
  padding: "8px 14px", background: "white", border: "1px solid var(--cc-gray-200)", borderRadius: 6,
  fontSize: 13, fontWeight: 600, color: "var(--cc-gray-700)", cursor: "pointer",
};

const overlay: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.45)",
  display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 20,
};

const modalBox: React.CSSProperties = {
  background: "white", borderRadius: 12, width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto",
  boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
};

const addModuleBtn: React.CSSProperties = {
  marginTop: 4, padding: "8px 10px", border: "1px dashed var(--cc-gray-300)", borderRadius: 6,
  background: "transparent", color: "var(--cc-teal)", fontSize: 12, fontWeight: 600,
  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, justifyContent: "center",
};
